import { useCallback } from 'react';

import type { UseFormReturn } from 'react-hook-form';

import { currencyInput } from '@/_schemas/generic';

import type { AgreementFormSchema } from '../schemas';
import type { SubsidyItemsFormSchema } from '../schemas/subsidy-items-schemas';
import {
  useSubsidyItemsBulkCreateMutation,
  useSubsidyItemsBulkDeleteMutation,
  useSubsidyItemsBulkUpdateMutation,
} from '../subsidies-items-apis';

export function useSubsidyItems(form: UseFormReturn<AgreementFormSchema>) {
  const deleteMutation = useSubsidyItemsBulkDeleteMutation();
  const updateMutation = useSubsidyItemsBulkUpdateMutation();
  const createMutation = useSubsidyItemsBulkCreateMutation();

  const createSubsidyItems = useCallback(
    async (created: SubsidyItemsFormSchema[], subsidyId: number): Promise<SubsidyItemsFormSchema[]> => {
      const res = await createMutation.mutateAsync({
        items: created.map((c) => ({
          subsidy_id: subsidyId,
          agreement_deliverable_id: c.agreement_deliverable_id,
          amount: currencyInput.formToUpdateConverter(c.amount),
          percentage: currencyInput.formToUpdateConverter(c.percentage),
          quantity: currencyInput.formToUpdateConverter(c.quantity),
          start_date: c.start_date,
          end_date: c.end_date,
        })),
      });

      const newlyCreated = res.map((r, idx) => {
        const original = created[idx] ?? ({} as SubsidyItemsFormSchema);
        return {
          ...original,
          id: r.id,
        } as SubsidyItemsFormSchema;
      });

      return newlyCreated;
    },
    [createMutation]
  );

  const manageSubsidyItems = useCallback(
    async (subsidyId: number, oldSubsidyItemsId: number[]) => {
      const subsidies = form.getValues(`subsidies`) || [];
      const subsidyIndex = subsidies.findIndex((s) => s.id === subsidyId);
      const subsidyItems = form.getValues(`subsidies.${subsidyIndex}.subsidy_deliverables`) || [];

      const created = subsidyItems.filter((t) => t.id < 0);

      const deleted = oldSubsidyItemsId.filter((oId) => !subsidyItems.find((t) => t.id === oId));
      const updated = subsidyItems.filter((t) => t.id > 0);

      if (deleted && deleted.length > 0) {
        await deleteMutation.mutateAsync({
          items: deleted,
        });
      }

      if (updated.length > 0) {
        await updateMutation.mutateAsync({
          items: updated.map((c) => ({
            id: c.id,
            subsidy_id: subsidyId,
            agreement_deliverable_id: c.agreement_deliverable_id,
            amount: currencyInput.formToUpdateConverter(c.amount),
            percentage: currencyInput.formToUpdateConverter(c.percentage),
            quantity: currencyInput.formToUpdateConverter(c.quantity),
            start_date: c.start_date,
            end_date: c.end_date,
          })),
        });
      }

      let newlyCreated: SubsidyItemsFormSchema[] = [];
      if (created.length > 0) {
        newlyCreated = await createSubsidyItems(created, subsidyId);
      }

      const latestItems = [...updated, ...newlyCreated];
      form.setValue(`subsidies.${subsidyIndex}.subsidy_deliverables`, latestItems, { shouldDirty: false });
      return latestItems;
    },
    [form, deleteMutation, updateMutation, createSubsidyItems]
  );

  return {
    createSubsidyItems,
    manageSubsidyItems,
    isManageSubsidyItemsPending: deleteMutation.isPending || updateMutation.isPending || createMutation.isPending,
  };
}
